<script lang="ts">
export default {
  inheritAttrs: false,
};
</script>

<script setup lang="ts">
import { computed, ref } from 'vue';

const props = defineProps({
  label: String,
  labelId: String,
  color: String,
  vertical: {
    type: Boolean,
    default: false,
  },
  icon: {
    type: Object,
    default: null,
  },
  modelValue: {},
  value: {},
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue', 'change']);

const isSelected = computed(() => props.modelValue == props.value);

const modelProxy = computed({
  get: () => props.modelValue,
  set(val) {},
});

function onChange() {
  emit('update:modelValue', props.value);
  emit('change', props.value);
}

const classes = computed(() => {
  return {
    'radio-label': true,
    'radio-selected': isSelected.value,
    [`radio-${props.color}`]: true,
    flex: true,
    'flex-col': props.vertical,
    'gap-y-2': props.vertical,
    'items-center': true,
    'py-3': true,
    'cursor-pointer': true,
    'transition-all': true,
    'duration-200': true,
  };
});

const radioCircleClasses = computed(() => {
  return [
    'radio-circle',
    'w-6 h-6 rounded-full border-2 border-solid mr-3 flex items-center justify-center',
    'transition-all duration-200',
  ];
});

const radioInnerClasses = computed(() => {
  return [
    'radio-inner',
    'w-3 h-3 rounded-full transition-all duration-200',
  ];
});
</script>

<template>
  <label :class="classes" @click="onChange">
    <input
      class="sr-only"
      type="radio"
      :aria-labelledby="labelId"
      :data-colorscheme="color"
      v-bind="$attrs"
      :value="value"
      v-model="modelProxy"
    />
    <span :class="radioCircleClasses">
      <span
        v-if="isSelected"
        :class="radioInnerClasses"
      ></span>
      <span v-else-if="loading" class="animate-pulse w-2 h-2 rounded-full bg-gray-300"></span>
    </span>

    <span :id="labelId" v-html="label" class="radio-text flex-1 pointer-events-none text-base"></span>
    <template v-if="typeof icon !== 'undefined'">
      <component :is="icon" class="ml-auto mr-2 pointer-events-none" />
    </template>
  </label>
</template>

<style scoped lang="postcss">
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.radio-label {
  position: relative;
  padding: 0.75rem 0.5rem;
  border-radius: 0.5rem;
  transition: all 0.2s ease;
  justify-content: flex-start;
  text-align: left;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

/* Only apply hover effects on non-touch devices */
@media (hover: hover) and (pointer: fine) {
  .radio-label:hover {
    background-color: rgba(0, 0, 0, 0.03);
  }
}

.radio-label:active {
  transform: scale(0.98);
}

.radio-selected {
  background-color: rgba(0, 0, 0, 0.05);
}

.radio-circle {
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border-color: #ab8008 !important;
}

.radio-inner {
  background-color: #ab8008;
}

.radio-selected .radio-circle {
  transform: scale(1.1);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.radio-inner {
  animation: popIn 0.2s ease-out;
}

@keyframes popIn {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.radio-text {
  font-weight: 500;
  color: #333;
  line-height: 1.4;
}

.radio-selected .radio-text {
  color: #111;
  font-weight: 600;
}

/* Color-specific hover states - only on non-touch devices */
@media (hover: hover) and (pointer: fine) {
  .radio-yellow:hover {
    background-color: rgba(253, 200, 51, 0.1);
  }
  .radio-green:hover {
    background-color: rgba(114, 162, 49, 0.1);
  }
  .radio-blue:hover {
    background-color: rgba(43, 153, 132, 0.1);
  }
  .radio-purple:hover {
    background-color: rgba(141, 62, 121, 0.1);
  }
}

.radio-yellow.radio-selected {
  background-color: rgba(253, 200, 51, 0.15);
}
.radio-green.radio-selected {
  background-color: rgba(114, 162, 49, 0.15);
}
.radio-blue.radio-selected {
  background-color: rgba(43, 153, 132, 0.15);
}
.radio-purple.radio-selected {
  background-color: rgba(141, 62, 121, 0.15);
}
</style>
